import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntake, type PrescreenData } from "../lib/intake-context-value";
import { API_BASE } from "../lib/api";
import { fireEvent } from "../lib/tracking";


export default function PrescreenPage() {
  const navigate = useNavigate();
  const { token, setPrescreen, setPrescreenResult } = useIntake();

  const [age, setAge] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [weightLb, setWeightLb] = useState("");
  const [sex, setSex] = useState("");
  const [medications, setMedications] = useState("");
  const [diagnoses, setDiagnoses] = useState<string[]>(["type_2_diabetes"]);
  const [onInsulin, setOnInsulin] = useState(false);
  // Play 2 — GLP-1 Recovery segment inputs.
  // glp1Status:
  //   "yes"     → patient has taken a GLP-1; date + reason required.
  //   "no"      → patient has never taken a GLP-1; clear out date/reason.
  //   "unsure"  → undetermined; backend treats `previousGlp1Use` as undefined
  //               and the screening team confirms during follow-up.
  const [glp1Status, setGlp1Status] = useState<"" | "yes" | "no" | "unsure">("");
  const [glp1LastUseDate, setGlp1LastUseDate] = useState("");
  const [glp1Reason, setGlp1Reason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const FIVE_YEARS_AGO = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 5);
    return d.toISOString().slice(0, 10);
  })();

  const bmi = (() => {
    const h = parseFloat(heightIn);
    const w = parseFloat(weightLb);
    if (!h || !w || h <= 0) return null;
    return Number(((703 * w) / (h * h)).toFixed(1));
  })();

  const handleSubmit = async () => {
    setError(null);
    setWarning(null);
    const ageNum = parseInt(age, 10);
    if (!ageNum || ageNum < 1 || ageNum > 120) {
      setError("Please enter a valid age between 1 and 120.");
      return;
    }
    if (!bmi || bmi <= 0) {
      setError("Please enter valid height and weight to calculate BMI.");
      return;
    }
    if (!sex) {
      setError("Please select your sex.");
      return;
    }

    // Play 2 — GLP-1 history validation.
    if (glp1Status === "yes") {
      if (!glp1LastUseDate) {
        setError("Please tell us when you last took a GLP-1 medication.");
        return;
      }
      if (glp1LastUseDate > today) {
        setError("Last-use date cannot be in the future.");
        return;
      }
      if (glp1LastUseDate < FIVE_YEARS_AGO) {
        // Allow but warn — long lookback is fine, just unusual.
        setWarning("Just confirming — that's more than 5 years ago.");
      }
      if (!glp1Reason) {
        setError("Please tell us the main reason you stopped.");
        return;
      }
    }

    const meds = medications
      .split(",")
      .map((m) => m.trim().toLowerCase())
      .filter(Boolean);

    // Strip GLP-1 fields when the user said "No". Avoids back-end thinking
    // a stale date/reason from a typo'd "Yes" flow is meaningful.
    const previousGlp1Use =
      glp1Status === "yes" ? true : glp1Status === "no" ? false : undefined;
    const lastUseDate = glp1Status === "yes" ? glp1LastUseDate : undefined;
    const reason = glp1Status === "yes" ? glp1Reason : undefined;

    const payload: PrescreenData = {
      age: ageNum,
      bmi,
      sex,
      medications: meds,
      diagnoses,
      onInsulin,
      ...(previousGlp1Use !== undefined && { previousGlp1Use }),
      ...(lastUseDate && { glp1LastUseDate: lastUseDate }),
      ...(reason && { glp1DiscontinuationReason: reason }),
    };

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/intake/vesper5/prescreen`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json.success) {
        setError(json.error || "Prescreen failed.");
        return;
      }
      setPrescreen(payload);
      setPrescreenResult(json.data);
      fireEvent(
        "prescreen_complete",
        "vesper5-us",
        { qualified: json.data.status === "qualified" },
        token
      );
      if (json.data.nextStep === "consent") {
        navigate("/consent");
      } else {
        // Pass segment hint so QualifiedPage / NotQualifiedPage can render
        // the GLP-1 Recovery copy variant when applicable.
        const seg = json.data.segment === "glp1_recovery" ? "glp1rec" : "standard";
        navigate(`/not-qualified-registry?seg=${seg}`);
      }
    } catch (e) { console.error("[intake] request failed", e);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-[24px] font-extralight text-gray-900" style={{ letterSpacing: "-0.4px" }}>
          Health Survey
        </h1>
        <p className="text-[14px] text-gray-600">
          A few questions to see if you may qualify.
        </p>
      </div>

      {error && (
        <div className="bg-[var(--color-status-danger-bg)] rounded-xl p-3 text-[13px] text-[var(--color-status-danger-text)]" role="alert">
          {error}
        </div>
      )}
      {warning && !error && (
        <div className="bg-[var(--color-status-caution-bg)] rounded-xl p-3 text-[13px] text-[var(--color-status-caution-text)]" role="status">
          {warning}
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 space-y-5" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <div className="space-y-2">
          <label htmlFor="age" className="block text-[13px] font-medium text-gray-700">
            Age
          </label>
          <input
            id="age"
            type="number"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
            placeholder="e.g. 42"
            aria-required="true"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="height" className="block text-[13px] font-medium text-gray-700">
              Height (inches)
            </label>
            <input
              id="height"
              type="number"
              value={heightIn}
              onChange={(e) => setHeightIn(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
              placeholder="e.g. 68"
              aria-required="true"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="weight" className="block text-[13px] font-medium text-gray-700">
              Weight (lbs)
            </label>
            <input
              id="weight"
              type="number"
              value={weightLb}
              onChange={(e) => setWeightLb(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
              placeholder="e.g. 210"
              aria-required="true"
            />
          </div>
        </div>

        {bmi !== null && (
          <div className="text-[13px] text-gray-500">
            Calculated BMI: <span className="font-medium text-gray-900">{bmi}</span>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="sex" className="block text-[13px] font-medium text-gray-700">
            Sex
          </label>
          <select
            id="sex"
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)] bg-white"
            aria-required="true"
          >
            <option value="">Select...</option>
            <option value="F">Female</option>
            <option value="M">Male</option>
            <option value="O">Other</option>
          </select>
        </div>

        {/* Play 2 — GLP-1 history. Sits BEFORE the meds free-text so the
            structured answers don't get lost in a comma-separated list. */}
        <div className="space-y-2">
          <span id="glp1-status-label" className="block text-[13px] font-medium text-gray-700">
            Have you ever taken Ozempic, Wegovy, Mounjaro, Zepbound, semaglutide,
            tirzepatide, or a similar weight-loss injection?
          </span>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-4" role="radiogroup" aria-labelledby="glp1-status-label">
            {([
              { v: "yes", label: "Yes" },
              { v: "no", label: "No" },
              { v: "unsure", label: "I'm not sure" },
            ] as const).map((opt) => (
              <label key={opt.v} className="flex items-center gap-2 text-[14px] text-gray-600 min-h-[40px]">
                <input
                  type="radio"
                  name="glp1Status"
                  value={opt.v}
                  checked={glp1Status === opt.v}
                  onChange={() => {
                    setGlp1Status(opt.v);
                    setError(null);
                    setWarning(null);
                  }}
                  className="w-4 h-4"
                />
                {opt.label}
              </label>
            ))}
          </div>
          {glp1Status === "unsure" && (
            <p className="text-[12px] text-gray-500 text-pretty">
              That's okay — you can leave the next questions blank, and our team
              can help confirm during screening.
            </p>
          )}
        </div>

        {glp1Status === "yes" && (
          <>
            <div className="space-y-2">
              <label htmlFor="glp1-last-use" className="block text-[13px] font-medium text-gray-700">
                When did you last take it?
              </label>
              <input
                id="glp1-last-use"
                type="date"
                value={glp1LastUseDate}
                onChange={(e) => setGlp1LastUseDate(e.target.value)}
                max={today}
                min="2010-01-01"
                className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
                aria-required="true"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="glp1-reason" className="block text-[13px] font-medium text-gray-700">
                What was the main reason you stopped?
              </label>
              <select
                id="glp1-reason"
                value={glp1Reason}
                onChange={(e) => setGlp1Reason(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)] bg-white"
                aria-required="true"
              >
                <option value="">Select...</option>
                <option value="cost">Cost or insurance</option>
                <option value="side_effects">Side effects</option>
                <option value="plateau">Stopped working / plateau</option>
                <option value="lifestyle_change">Lifestyle change</option>
                <option value="physician_advised">Physician advised stopping</option>
                <option value="other">Other</option>
              </select>
            </div>
          </>
        )}

        <div className="space-y-2">
          <label htmlFor="medications" className="block text-[13px] font-medium text-gray-700">
            Current medications (comma separated)
          </label>
          <input
            id="medications"
            type="text"
            value={medications}
            onChange={(e) => setMedications(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
            placeholder="e.g. metformin, lisinopril"
            aria-required="true"
          />
          <p className="text-[12px] text-gray-500">
            Include all prescription and over-the-counter medications.
          </p>
        </div>

        <div className="space-y-2">
          <span className="block text-[13px] font-medium text-gray-700">
            Diagnoses
          </span>
          <label className="flex items-center gap-2 text-[14px] text-gray-600">
            <input
              type="checkbox"
              checked={diagnoses.includes("type_2_diabetes")}
              onChange={(e) => {
                setDiagnoses((prev) =>
                  e.target.checked
                    ? [...prev, "type_2_diabetes"]
                    : prev.filter((d) => d !== "type_2_diabetes")
                );
              }}
              className="w-4 h-4 rounded border-gray-300"
            />
            Type 2 Diabetes
          </label>
          <label className="flex items-center gap-2 text-[14px] text-gray-600">
            <input
              type="checkbox"
              checked={diagnoses.includes("obesity")}
              onChange={(e) => {
                setDiagnoses((prev) =>
                  e.target.checked
                    ? [...prev, "obesity"]
                    : prev.filter((d) => d !== "obesity")
                );
              }}
              className="w-4 h-4 rounded border-gray-300"
            />
            Obesity
          </label>
        </div>

        <div className="space-y-2">
          <span className="block text-[13px] font-medium text-gray-700">
            Are you currently using insulin?
          </span>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-[14px] text-gray-600">
              <input
                type="radio"
                name="insulin"
                checked={onInsulin === true}
                onChange={() => setOnInsulin(true)}
                className="w-4 h-4"
              />
              Yes
            </label>
            <label className="flex items-center gap-2 text-[14px] text-gray-600">
              <input
                type="radio"
                name="insulin"
                checked={onInsulin === false}
                onChange={() => setOnInsulin(false)}
                className="w-4 h-4"
              />
              No
            </label>
          </div>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full py-3 rounded-xl bg-gray-900 text-white text-[14px] font-medium hover:bg-gray-800 active:scale-[0.97] transition-[transform,opacity,background-color] duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? "Checking..." : "Continue"}
      </button>
    </div>
  );
}
